import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { DateTimePicker } from '@/components/ui/DateTimePicker'

const TYPE_LABELS: Record<string, string> = {
  INITIALE: 'Formation initiale',
  RECYCLAGE: 'Recyclage',
  HABILITATION: 'Habilitation',
  SENSIBILISATION: 'Sensibilisation',
  AUTRE: 'Autre',
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  PLANNED:     { label: 'Planifiée',   color: 'bg-accent/10 border-accent/25 text-accent' },
  IN_PROGRESS: { label: 'En cours',    color: 'bg-warning/10 border-warning/25 text-warning' },
  COMPLETED:   { label: 'Terminée',    color: 'bg-success/10 border-success/25 text-success' },
  CANCELLED:   { label: 'Annulée',     color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
}

interface Session {
  id: string
  title: string
  type: string
  status: string
  startDate: string
  endDate: string
  durationH: number
  city: string | null
  isRemote: boolean
  formateurName: string | null
  _count: { trainees: number }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function CentreFormationPage() {
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pendingTrainees, setPendingTrainees] = useState<{ firstName: string; lastName: string; employer: string }[]>([])
  const [traineeInput, setTraineeInput] = useState({ firstName: '', lastName: '', employer: '' })

  async function load() {
    try {
      const data = await apiFetch<Session[]>('/training/sessions')
      setSessions(data)
    } catch { /* silencieux */ }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  function addPendingTrainee() {
    const { firstName, lastName } = traineeInput
    if (!firstName.trim() || !lastName.trim()) return
    setPendingTrainees((prev) => [...prev, { ...traineeInput }])
    setTraineeInput({ firstName: '', lastName: '', employer: '' })
  }

  function removePendingTrainee(idx: number) {
    setPendingTrainees((prev) => prev.filter((_, i) => i !== idx))
  }

  async function handleCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSaving(true); setError('')
    const fd = new FormData(e.currentTarget)
    try {
      const session = await apiFetch<{ id: string }>('/training/sessions', {
        method: 'POST',
        body: JSON.stringify({
          title: fd.get('title'),
          type: fd.get('type'),
          startDate: fd.get('startDate') as string,
          endDate: fd.get('endDate') as string,
          durationH: Number(fd.get('durationH')),
          isRemote: fd.get('isRemote') === 'on',
          city: fd.get('city') || undefined,
          address: fd.get('address') || undefined,
          formateurName: fd.get('formateurName') || undefined,
          objectifs: fd.get('objectifs') || undefined,
          capacity: fd.get('capacity') ? Number(fd.get('capacity')) : undefined,
        }),
      })
      for (const t of pendingTrainees) {
        await apiFetch(`/training/sessions/${session.id}/trainees`, {
          method: 'POST',
          body: JSON.stringify({
            firstName: t.firstName,
            lastName: t.lastName,
            employer: t.employer || undefined,
          }),
        })
      }
      setShowForm(false)
      setPendingTrainees([])
      setTraineeInput({ firstName: '', lastName: '', employer: '' })
      ;(e.target as HTMLFormElement).reset()
      await load()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur')
    } finally {
      setSaving(false)
    }
  }

  const planned = sessions.filter((s) => s.status === 'PLANNED')
  const inProgress = sessions.filter((s) => s.status === 'IN_PROGRESS')
  const completed = sessions.filter((s) => s.status === 'COMPLETED')

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Sessions de formation</h1>
          <p className="text-text-secondary text-sm mt-1">Gérez vos sessions, stagiaires et conformité Qualiopi</p>
        </div>
        <div className="flex gap-2">
          <Link to="/qualiopi" className="glass-btn px-4 py-2.5 text-sm rounded-xl flex items-center gap-2">
            <span>🏅</span> Qualiopi
          </Link>
          <button
            onClick={() => { setShowForm((v) => !v); setPendingTrainees([]); setTraineeInput({ firstName: '', lastName: '', employer: '' }) }}
            className="glass-btn-accent px-4 py-2.5 text-sm rounded-xl"
          >
            {showForm ? 'Annuler' : '+ Nouvelle session'}
          </button>
        </div>
      </div>

      {/* Stats rapides */}
      {!loading && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Planifiées', value: planned.length, color: 'text-accent' },
            { label: 'En cours', value: inProgress.length, color: 'text-warning' },
            { label: 'Terminées', value: completed.length, color: 'text-success' },
          ].map((s) => (
            <div key={s.label} className="glass-card p-4 text-center">
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-text-muted text-xs mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Formulaire nouvelle session */}
      {showForm && (
        <div className="glass-card p-6">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-5">Nouvelle session</h2>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="glass-label">Intitulé de la formation *</label>
              <input name="title" className="glass-input" required placeholder="Ex : Formation SST initiale" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Type *</label>
                <select name="type" className="glass-input" required>
                  <option value="INITIALE">Formation initiale</option>
                  <option value="RECYCLAGE">Recyclage</option>
                  <option value="HABILITATION">Habilitation</option>
                  <option value="SENSIBILISATION">Sensibilisation</option>
                  <option value="AUTRE">Autre</option>
                </select>
              </div>
              <div>
                <label className="glass-label">Durée (heures) *</label>
                <input name="durationH" type="number" min="0.5" step="0.5" className="glass-input" required placeholder="Ex : 7" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Date de début *</label>
                <DateTimePicker name="startDate" required placeholder="Début de session…" />
              </div>
              <div>
                <label className="glass-label">Date de fin *</label>
                <DateTimePicker name="endDate" required placeholder="Fin de session…" align="right" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Ville</label>
                <input name="city" className="glass-input" placeholder="Lyon" />
              </div>
              <div>
                <label className="glass-label">Capacité max</label>
                <input name="capacity" type="number" min="1" className="glass-input" placeholder="12" />
              </div>
            </div>
            <div>
              <label className="glass-label">Nom du formateur</label>
              <input name="formateurName" className="glass-input" placeholder="Prénom Nom" />
            </div>
            <div>
              <label className="glass-label">Objectifs pédagogiques</label>
              <textarea name="objectifs" className="glass-input min-h-[80px]" placeholder="À l'issue de cette formation, le stagiaire sera capable de…" />
            </div>
            <div className="flex items-center gap-3">
              <input type="checkbox" name="isRemote" id="isRemote" className="w-4 h-4 accent-accent" />
              <label htmlFor="isRemote" className="text-sm text-text-secondary cursor-pointer">Formation à distance (visioconférence)</label>
            </div>

            {/* Stagiaires */}
            <div className="border-t border-white/[0.08] pt-4">
              <label className="glass-label mb-3 block">Stagiaires <span className="text-text-muted font-normal">(optionnel)</span></label>
              <div className="flex gap-2 mb-3">
                <input
                  value={traineeInput.firstName}
                  onChange={(e) => setTraineeInput((v) => ({ ...v, firstName: e.target.value }))}
                  className="glass-input text-sm flex-1"
                  placeholder="Prénom *"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPendingTrainee())}
                />
                <input
                  value={traineeInput.lastName}
                  onChange={(e) => setTraineeInput((v) => ({ ...v, lastName: e.target.value }))}
                  className="glass-input text-sm flex-1"
                  placeholder="Nom *"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPendingTrainee())}
                />
                <input
                  value={traineeInput.employer}
                  onChange={(e) => setTraineeInput((v) => ({ ...v, employer: e.target.value }))}
                  className="glass-input text-sm flex-1"
                  placeholder="Employeur"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPendingTrainee())}
                />
                <button
                  type="button"
                  onClick={addPendingTrainee}
                  disabled={!traineeInput.firstName.trim() || !traineeInput.lastName.trim()}
                  className="glass-btn px-4 py-2 text-sm rounded-xl whitespace-nowrap disabled:opacity-40"
                >
                  + Ajouter
                </button>
              </div>
              {pendingTrainees.length > 0 && (
                <div className="space-y-1.5">
                  {pendingTrainees.map((t, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.07]">
                      <div className="w-7 h-7 rounded-full bg-accent/15 border border-accent/25 flex items-center justify-center flex-shrink-0">
                        <span className="text-accent text-[10px] font-bold">{t.firstName[0]}{t.lastName[0]}</span>
                      </div>
                      <span className="text-text-secondary text-sm flex-1">
                        <span className="font-medium">{t.lastName.toUpperCase()}</span> {t.firstName}
                        {t.employer && <span className="text-text-muted ml-2 text-xs">· {t.employer}</span>}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePendingTrainee(i)}
                        className="text-text-muted hover:text-danger transition-colors text-xs px-1.5 py-1 rounded-lg hover:bg-danger/10"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <p className="text-text-muted text-xs mt-1">{pendingTrainees.length} stagiaire{pendingTrainees.length > 1 ? 's' : ''} ajouté{pendingTrainees.length > 1 ? 's' : ''}</p>
                </div>
              )}
            </div>

            {error && <p className="text-danger text-sm">{error}</p>}
            <button type="submit" disabled={saving} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
              {saving ? 'Création…' : `Créer la session${pendingTrainees.length > 0 ? ` + ${pendingTrainees.length} stagiaire${pendingTrainees.length > 1 ? 's' : ''}` : ''}`}
            </button>
          </form>
        </div>
      )}

      {/* Liste des sessions */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="glass-card h-20 animate-pulse" />)}
        </div>
      ) : sessions.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <p className="text-4xl mb-4">🎓</p>
          <p className="text-text-primary font-medium mb-1">Aucune session créée</p>
          <p className="text-text-secondary text-sm">Créez votre première session de formation pour commencer.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => {
            const cfg = STATUS_CONFIG[s.status]
            return (
              <Link
                key={s.id}
                to={`/sessions/${s.id}`}
                className="glass-card p-4 flex items-center gap-4 hover:bg-white/[0.06] transition-colors block"
              >
                <div className="w-10 h-10 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg">🎓</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-text-primary text-sm font-semibold truncate">{s.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border flex-shrink-0 ${cfg.color}`}>
                      {cfg.label}
                    </span>
                  </div>
                  <p className="text-text-muted text-xs">
                    {TYPE_LABELS[s.type]} · {formatDate(s.startDate)} → {formatDate(s.endDate)}
                    {s.isRemote ? ' · À distance' : s.city ? ` · ${s.city}` : ''}
                    {s.formateurName ? ` · ${s.formateurName}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right">
                    <p className="text-text-primary text-sm font-bold">{s._count.trainees}</p>
                    <p className="text-text-muted text-[10px]">stagiaire{s._count.trainees > 1 ? 's' : ''}</p>
                  </div>
                  <span className="text-text-muted text-xs">{s.durationH}h</span>
                  <span className="text-text-muted text-sm">→</span>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
